import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useReveal } from "../useReveal";

// The transition + resting/entering states, shared so semantic elements that
// can't be a wrapping <div> (an <li> in a sequence, say) can self-reveal with the
// same motion. ease-out-expo for the premium, no-bounce settle.
export const REVEAL_TRANSITION =
  "transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none";

export function revealState(revealed: boolean): string {
  return revealed ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0";
}

// A soft rise-and-fade on scroll, wrapping a section's content. Visible by
// default (see useReveal) — this only adds the entrance for capable browsers.
// `delayMs` staggers siblings (e.g. cards in a row) without per-item observers
// fighting each other.
export function Reveal({
  children,
  className,
  delayMs = 0,
}: {
  children: ReactNode;
  className?: string;
  delayMs?: number;
}) {
  const { ref, revealed } = useReveal();
  return (
    <div
      ref={ref}
      data-revealed={revealed}
      className={cn(REVEAL_TRANSITION, revealState(revealed), className)}
      style={delayMs ? { transitionDelay: `${delayMs}ms` } : undefined}
    >
      {children}
    </div>
  );
}
